import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Card,
    CardContent,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';

/**
 * ResponsiveTable Component
 * 
 * Displays data as a table on desktop and as cards on mobile
 * 
 * Props:
 * - columns: Array of column definitions [{ field: 'name', header: 'Name', render: (row) => row.name }]
 * - mobileColumns: Optional array of column fields to show on mobile (if not provided, shows all columns)
 * - mobileHeader: Optional function to render custom header content (row, index) => <content/>
 * - data: Array of data rows
 * - actions: Function that returns action buttons for each row (row) => <buttons/>
 * - onRowClick: Optional function called when a row/card is clicked
 */
const ResponsiveTable = ({ columns, mobileColumns, mobileHeader, data, actions, onRowClick, stickyHeader = true }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 960px

    if (isMobile) {
        // Mobile: Card View with attractive gradient colors
        const cardColors = [
            { bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', badge: '#667eea' },
            { bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', badge: '#f5576c' },
            { bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', badge: '#4facfe' },
            { bg: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', badge: '#43e97b' },
            { bg: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', badge: '#fa709a' },
            { bg: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', badge: '#30cfd0' },
            { bg: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', badge: '#a8edea' },
            { bg: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)', badge: '#ff9a9e' },
            { bg: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', badge: '#fcb69f' },
            { bg: 'linear-gradient(135deg, #ff6e7f 0%, #bfe9ff 100%)', badge: '#ff6e7f' },
        ];

        // Filter columns for mobile view if mobileColumns is provided
        const displayColumns = mobileColumns 
            ? columns.filter(col => mobileColumns.includes(col.field))
            : columns;

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, mx: 0, backgroundColor: '#f8f9fa', pb: 2 }}>
                {data.map((row, index) => {
                    const colorScheme = cardColors[index % cardColors.length];

                    return (
                        <Card
                            key={row.Id || index}
                            onClick={() => onRowClick && onRowClick(row)}
                            sx={{
                                cursor: onRowClick ? 'pointer' : 'default',
                                transition: 'all 0.2s ease',
                                borderRadius: '12px',
                                overflow: 'hidden',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                                mx: 2,
                                mt: 2,
                                '&:hover': {
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
                                    transform: 'translateY(-2px)',
                                },
                                opacity: row.IsActive === false ? 0.6 : 1,
                                background: 'white',
                                mb: 0,
                            }}
                        >
                            {/* Colored Header with Gradient */}
                            <Box
                                sx={{
                                    background: colorScheme.bg,
                                    p: 2.5,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                }}
                            >
                                {mobileHeader ? (
                                    mobileHeader(row, index)
                                ) : (
                                    <>
                                        <Box
                                            sx={{
                                                bgcolor: 'rgba(255, 255, 255, 0.3)',
                                                color: 'white',
                                                px: 2,
                                                py: 0.5,
                                                borderRadius: '20px',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                backdropFilter: 'blur(10px)',
                                            }}
                                        >
                                            #{index + 1}
                                        </Box>
                                        {row.IsActive === false && (
                                            <Box
                                                sx={{
                                                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                                                    color: 'white',
                                                    px: 2,
                                                    py: 0.5,
                                                    borderRadius: '20px',
                                                    fontSize: '11px',
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                Inactive
                                            </Box>
                                        )}
                                    </>
                                )}
                            </Box>

                            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                {/* Data Fields - Only show selected columns on mobile */}
                                {displayColumns.map((column, colIndex) => (
                                    <Box
                                        key={column.field}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            py: 1.5,
                                            borderBottom: colIndex < displayColumns.length - 1 ? '1px solid #e5e7eb' : 'none',
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 600,
                                                color: '#6b7280',
                                                textTransform: 'uppercase',
                                                fontSize: '11px',
                                                letterSpacing: '0.5px',
                                                minWidth: '100px',
                                            }}
                                        >
                                            {column.header}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '14px',
                                                textAlign: 'right',
                                                flex: 1,
                                                ml: 2,
                                                color: '#111827',
                                            }}
                                        >
                                            {column.render ? column.render(row) : row[column.field]}
                                        </Typography>
                                    </Box>
                                ))}

                                {/* Actions */}
                                {actions && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            gap: 1.5,
                                            mt: 2.5,
                                            pt: 2.5,
                                            borderTop: '1px solid #e5e7eb',
                                        }}
                                    >
                                        {actions(row)}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    );
                })}
            </Box>
        );
    }

    // Desktop: Table View (matches original styling)
    return (
        <Box
            sx={{
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                maxHeight: '480px',
                overflowY: 'auto',
            }}
        >
            <TableContainer>
                <Table stickyHeader={stickyHeader}>
                    <TableHead>
                        <TableRow style={{ background: 'linear-gradient(to right, #051a20ff, #051a20ff)' }}>
                            <TableCell
                                style={{
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    borderBottom: '2px solid #1e293b',
                                    color: '#f8fafc',
                                    padding: '2px',
                                    textAlign: 'left',
                                    letterSpacing: '0.5px',
                                    backgroundColor: '#0f172a',
                                }}
                            >
                                Sl.No
                            </TableCell>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.field}
                                    style={{
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        borderBottom: '2px solid #1e293b',
                                        color: '#f8fafc',
                                        padding: '2px',
                                        textAlign: 'left',
                                        letterSpacing: '0.5px',
                                        backgroundColor: '#0f172a',
                                    }}
                                >
                                    {column.header}
                                </TableCell>
                            ))}
                            {actions && (
                                <TableCell
                                    style={{
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        borderBottom: '2px solid #1e293b',
                                        color: '#f8fafc',
                                        padding: '2px',
                                        textAlign: 'left',
                                        letterSpacing: '0.5px',
                                        backgroundColor: '#0f172a',
                                    }}
                                >
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ backgroundColor: '#F1EFEC' }}>
                        {data.map((row, index) => (
                            <TableRow
                                key={row.Id || index}
                                onClick={() => onRowClick && onRowClick(row)}
                                className={row.IsActive === false ? 'inactiveRow' : ''}
                                sx={{
                                    '&:hover': {
                                        backgroundColor: 'white',
                                    },
                                    cursor: onRowClick ? 'pointer' : 'default',
                                }}
                            >
                                <TableCell className='table-header-cell.child' style={{ padding: '3px' }}>
                                    {index + 1}
                                </TableCell>
                                {columns.map((column) => (
                                    <TableCell
                                        key={column.field}
                                        className='table-header-cell.child'
                                        style={{ padding: '3px' }}
                                    >
                                        {column.render ? column.render(row) : row[column.field]}
                                    </TableCell>
                                ))}
                                {actions && (
                                    <TableCell
                                        className='table-header-cell.child-align-right'
                                        style={{ padding: '3px', textAlign: 'right' }}
                                    >
                                        {actions(row)}
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default ResponsiveTable;
